import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface MetaEngagementDecosHistogramDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/**
 * Decos placed histogram for META Productive Users.
 *
 * One numeric column per selected day: d0, d1, …, d14 (user count).
 */
export class MetaEngagementDecosHistogramDataLoaderFactory extends Factory<
    DataLoader,
    MetaEngagementDecosHistogramDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'MetaEngagementDecosHistogramDataLoaderFactory';

    getInstance(options: MetaEngagementDecosHistogramDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                cancellationToken.throwIfCancellationRequested();

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const selectedDaysAndSessions =
                    queryContext.settings[
                        QueryContextSettingID
                            .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                    ] ??
                    QueryContextDefaultSettings[
                        QueryContextSettingID
                            .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                    ]!;

                const selectedDaysAndSessionsSQL: Partial<ICompiledSQLTokens> =
                    {
                        where: [
                            MultipleSelectComponent.toNumberSQL('install_day_no', {
                                value: {
                                    options: selectedDaysAndSessions,
                                    filterType: 'include',
                                },
                                isFilterApplied: true,
                                isSplitApplied: true,
                            }),
                        ],
                    };

                /** 3 AS d3, 7 AS d7, 30 AS d30 */
                const pivotColumns = selectedDaysAndSessions
                    .map((d) => `${d} AS d${d}`)
                    .join(',');

                const { splitSelect, splitGroupBy, where, join } =
                    BiVizDashboardUsersActivity.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options,
                        selectedDaysAndSessionsSQL
                    );

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH base AS (
  SELECT
    ${splitGroupBy}day_no,
    CASE
      WHEN new_decos_placed >= 15 THEN '15+'
      ELSE CAST(COALESCE(new_decos_placed, 0) AS STRING)
    END AS deco_count,
    COUNT(DISTINCT player_id) AS sample
  FROM 
  (
    SELECT
        ${splitSelect}install_day_no AS day_no,
        new_decos_placed,
        player_id
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        datasourceSqlMap.dataset,
        datasourceSqlMap.tableId
    )}\` ds
    ${join}
    ${where}
    )
  GROUP BY ${splitGroupBy}day_no, deco_count
)

SELECT *
FROM base
PIVOT (ANY_VALUE(sample) FOR day_no IN (${pivotColumns}))
ORDER BY ${splitGroupBy}SAFE_CAST(IF(deco_count = '15+', '99', deco_count) AS INT64)
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
